import { useEffect } from "react";
import { clearAuthStorage, directus, resetLoggingOutState } from "@/providers/directus";
import { useBrand } from "@/hooks/useBrand";

/**
 * Unconditional force-logout screen (/logout).
 *
 * Sits outside every <Authenticated> wrapper so it works from any state:
 * expired or mismatched tokens, stuck half-logged-in sessions (/tfa-setup,
 * /change-password), broken masquerade state, or stale role caches. Clears
 * localStorage/sessionStorage, revokes server cookies via /auth/logout, and
 * performs a hard redirect to /login?logged_out=1 so in-memory React Query
 * caches and SSE streams are torn down completely.
 */
export function ForceLogout() {
  const brand = useBrand();

  // Clear local storage synchronously during render so no child or concurrent
  // hook reads stale credentials while the server logout request is in flight.
  clearAuthStorage();
  resetLoggingOutState();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        clearAuthStorage();
        await Promise.race([
          directus.logout(),
          new Promise((resolve) => setTimeout(resolve, 1500)),
        ]);
      } catch {
        /* ignore — local storage is already cleared */
      } finally {
        clearAuthStorage();
        if (!cancelled && typeof window !== "undefined" && window.location) {
          window.location.replace("/login");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="login-page min-h-screen grid place-items-center p-6">
      <div className="w-full max-w-[380px] text-center">
        <div className="login-logo-shift flex items-center gap-2.5 mb-6 justify-center">
          <img src={brand.logoUrl} alt={brand.name} className="w-9 h-9 object-contain" />
          <div className="brand-wordmark text-xl">{brand.name}</div>
        </div>
        <div className="login-card rounded-lg p-6 space-y-2">
          <div className="text-sm font-medium text-foreground">Signing out &amp; resetting session…</div>
          <p className="text-xs text-muted-foreground">
            Clearing saved credentials and redirecting to sign in.
          </p>
        </div>
      </div>
    </div>
  );
}
